import React, {
  createContext, useContext, useState, useCallback,
  useEffect, useRef, ReactNode,
} from 'react';
import type { User, AuthTokens, CreateUserRequest, MeLeagueStats, DateFormat } from '@shootout-comps/types';
import type { KeyValueStorage } from '../storage/KeyValueStorage';
import type { OAuthLauncher } from './OAuthLauncher';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // True when user.role === 'superuser' AND the admin hasn't switched on the "preview as
  // non-admin" toggle. Every admin-only UI check in the app should read this (not user.role
  // directly) so the toggle takes effect everywhere consistently.
  isSuperuser: boolean;
  previewAsNonAdmin: boolean;
  setPreviewAsNonAdmin: (value: boolean) => void;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string, marketingConsent?: boolean) => Promise<{ username: string; email: string; devToken?: string }>;
  verifyEmail: (token: string) => Promise<{ message: string; username: string }>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  // OAuth (Google / Facebook) sign-up and login
  startOAuth: (provider: 'google' | 'facebook') => Promise<void>;
  exchangeOAuthCode: (code: string) => Promise<void>;
  // Returns a valid access token, refreshing if necessary
  getAccessToken: () => Promise<string | null>;
  // User management (superuser only)
  listUsers: () => Promise<User[]>;
  createUser: (req: CreateUserRequest) => Promise<User>;
  deleteUser: (id: string) => Promise<void>;
  changeUserPassword: (id: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  requestEmailChange: (newEmail: string) => Promise<string>;
  updateDateFormat: (dateFormat: DateFormat) => Promise<void>;
  // Which leagues/tournaments the signed-in user's linked player identities appear in.
  fetchLeagueStats: () => Promise<MeLeagueStats>;
  // Registers/unregisters this device's Expo push token so league admins can reach it via
  // POST /leagues/:id/notify. Mobile-only in practice (web has no equivalent token).
  registerPushToken: (token: string) => Promise<void>;
  unregisterPushToken: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const ACCESS_KEY  = 'ss_access_token';
const REFRESH_KEY = 'ss_refresh_token';

export interface AuthProviderProps {
  children: ReactNode;
  storage: KeyValueStorage;
  // Base URL to prefix onto '/api/auth' — '' for web (same-origin relative requests),
  // an absolute origin (e.g. 'https://api.example.com') for mobile.
  apiBaseUrl: string;
  // Which client is initiating OAuth — threaded onto the authorize URL as `?target=`
  // so the backend knows whether to redirect back to the web origin or the mobile deep link scheme.
  oauthTarget: 'web' | 'mobile';
  oauthLauncher: OAuthLauncher;
}

export function AuthProvider({ children, storage, apiBaseUrl, oauthTarget, oauthLauncher }: AuthProviderProps) {
  const API = `${apiBaseUrl}/api/auth`;
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewAsNonAdmin, setPreviewAsNonAdmin] = useState(false);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const isSuperuser = user?.role === 'superuser' && !previewAsNonAdmin;

  // ── Token storage helpers ──────────────────────────────────────────────────

  const storeTokens = useCallback(async (tokens: AuthTokens) => {
    await storage.setItem(ACCESS_KEY, tokens.accessToken);
    await storage.setItem(REFRESH_KEY, tokens.refreshToken);
    setUser(tokens.user);
  }, [storage]);

  const clearTokens = useCallback(async () => {
    await storage.removeItem(ACCESS_KEY);
    await storage.removeItem(REFRESH_KEY);
    setUser(null);
    setPreviewAsNonAdmin(false);
  }, [storage]);

  // ── Refresh logic (deduplicated) ──────────────────────────────────────────

  const doRefresh = useCallback(async (): Promise<string | null> => {
    const refreshToken = await storage.getItem(REFRESH_KEY);
    if (!refreshToken) return null;
    try {
      const res = await fetch(`${API}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) { await clearTokens(); return null; }
      const data = await res.json() as AuthTokens;
      await storeTokens(data);
      return data.accessToken;
    } catch {
      await clearTokens();
      return null;
    }
  }, [API, storage, clearTokens, storeTokens]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const token = await storage.getItem(ACCESS_KEY);
    if (!token) return null;

    // Decode exp without a library
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresInMs = payload.exp * 1000 - Date.now();
      if (expiresInMs > 30_000) return token; // still valid for >30s
    } catch {
      // malformed token — fall through to refresh
    }

    // Deduplicate concurrent refresh calls
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = doRefresh().finally(() => {
        refreshPromiseRef.current = null;
      });
    }
    return refreshPromiseRef.current;
  }, [storage, doRefresh]);

  // ── Bootstrap: restore session on mount ───────────────────────────────────

  useEffect(() => {
    const restore = async () => {
      const token = await getAccessToken();
      if (!token) { setIsLoading(false); return; }
      try {
        const res = await fetch(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const u = await res.json() as User;
          setUser(u);
        } else {
          await clearTokens();
        }
      } catch {
        await clearTokens();
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, []); // eslint-disable-line

  // ── Auth actions ──────────────────────────────────────────────────────────

  const login = useCallback(async (username: string, password: string): Promise<void> => {
    const res = await fetch(`${API}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json() as AuthTokens & { error?: string; reason?: string; email?: string };
    if (!res.ok) {
      // Structured errors for specific account states
      if (data.error === 'PENDING_VERIFICATION') throw Object.assign(new Error('PENDING_VERIFICATION'), { email: data.email });
      if (data.error === 'ACCOUNT_SUSPENDED')    throw Object.assign(new Error('ACCOUNT_SUSPENDED'), { reason: data.reason });
      if (data.error === 'ACCOUNT_DEACTIVATED')  throw new Error('ACCOUNT_DEACTIVATED');
      throw new Error(data.error ?? 'Login failed');
    }
    await storeTokens(data);
  }, [API, storeTokens]);

  const logout = useCallback(async () => { await clearTokens(); }, [clearTokens]);

  const exchangeOAuthCode = useCallback(async (code: string): Promise<void> => {
    const res = await fetch(`${API}/oauth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await res.json() as AuthTokens & { error?: string };
    if (!res.ok) throw new Error(data.error ?? 'OAuth sign-in failed');
    await storeTokens(data);
  }, [API, storeTokens]);

  const startOAuth = useCallback(async (provider: 'google' | 'facebook'): Promise<void> => {
    const authUrl = `${API}/oauth/${provider}?target=${oauthTarget}`;
    const result = await oauthLauncher.launch(provider, authUrl);
    if (!result) return; // web: fire-and-forget redirect, page is navigating away
    if ('error' in result) throw new Error(result.error);
    await exchangeOAuthCode(result.code);
  }, [API, oauthTarget, oauthLauncher, exchangeOAuthCode]);

  const signup = useCallback(async (username: string, email: string, password: string, marketingConsent = false) => {
    const res = await fetch(`${API}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, marketingConsent }),
    });
    const data = await res.json() as { message?: string; username?: string; email?: string; error?: string; devVerificationToken?: string };
    if (!res.ok) throw new Error(data.error ?? 'Signup failed');
    // In dev, log the verification token so you can test without real email
    if (data.devVerificationToken) {
      console.info('%c📧 DEV: Verification token (no email needed):', 'color:#c9a84c;font-weight:bold', data.devVerificationToken);
    }
    return { username: data.username ?? '', email: data.email ?? '', devToken: data.devVerificationToken };
  }, [API]);

  const verifyEmail = useCallback(async (token: string) => {
    const res = await fetch(`${API}/verify-email?token=${encodeURIComponent(token)}`);
    const data = await res.json() as { message?: string; username?: string; error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Verification failed');
    return { message: data.message ?? '', username: data.username ?? '' };
  }, [API]);

  const resendVerification = useCallback(async (email: string) => {
    const res = await fetch(`${API}/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const data = await res.json() as { error?: string };
      throw new Error(data.error ?? 'Failed to resend');
    }
  }, [API]);

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    const res = await fetch(`${API}/request-password-reset`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const data = await res.json() as { error?: string };
      throw new Error(data.error ?? 'Failed');
    }
  }, [API]);

  // ── Authenticated fetch helper ────────────────────────────────────────────

  const authFetch = useCallback(async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json() as T & { error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Request failed');
    return data;
  }, [API, getAccessToken]);

  // ── User management ───────────────────────────────────────────────────────

  const listUsers = useCallback(() => authFetch<User[]>('/users'), [authFetch]);

  const createUser = useCallback((req: CreateUserRequest) =>
    authFetch<User>('/users', { method: 'POST', body: JSON.stringify(req) }),
  [authFetch]);

  const deleteUser = useCallback((id: string) =>
    authFetch<void>(`/users/${id}`, { method: 'DELETE' }),
  [authFetch]);

  const changeUserPassword = useCallback((id: string, password: string) =>
    authFetch<void>(`/users/${id}/password`, { method: 'PUT', body: JSON.stringify({ password }) }),
  [authFetch]);

  const requestEmailChange = useCallback(async (newEmail: string): Promise<string> => {
    const data = await authFetch<{ message: string }>('/request-email-change', {
      method: 'POST', body: JSON.stringify({ newEmail }),
    });
    return data.message;
  }, [authFetch]);

  const fetchLeagueStats = useCallback(() => authFetch<MeLeagueStats>('/me/league-stats'), [authFetch]);

  const updateDateFormat = useCallback(async (dateFormat: DateFormat): Promise<void> => {
    const updated = await authFetch<User>('/me/preferences', {
      method: 'PUT', body: JSON.stringify({ dateFormat }),
    });
    setUser(updated);
  }, [authFetch]);

  const registerPushToken = useCallback(async (token: string): Promise<void> => {
    await authFetch<{ success: boolean }>('/me/push-token', { method: 'POST', body: JSON.stringify({ token }) });
  }, [authFetch]);

  const unregisterPushToken = useCallback(async (token: string): Promise<void> => {
    await authFetch<{ success: boolean }>('/me/push-token', { method: 'DELETE', body: JSON.stringify({ token }) });
  }, [authFetch]);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading,
      isSuperuser, previewAsNonAdmin, setPreviewAsNonAdmin,
      login, logout, signup, verifyEmail, resendVerification, requestPasswordReset, getAccessToken,
      startOAuth, exchangeOAuthCode,
      listUsers, createUser, deleteUser, changeUserPassword, requestEmailChange,
      fetchLeagueStats, updateDateFormat, registerPushToken, unregisterPushToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
